import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Trophy, Globe, Users as UsersIcon, BookOpen, Flame } from "lucide-react";
import { API, useAuth, authFetch } from "@/App";

export default function Leaderboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [scope, setScope] = useState("global"); // "global" | groupId
  const [groups, setGroups] = useState([]);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const res = await authFetch(`${API}/groups`);
      if (res.ok) setGroups(await res.json());
    })();
  }, []);

  useEffect(() => {
    setIsLoading(true);
    (async () => {
      try {
        const url = scope === "global"
          ? `${API}/leaderboard?scope=global&limit=50`
          : `${API}/leaderboard?scope=group&group_id=${scope}&limit=200`;
        const res = await authFetch(url);
        if (res.ok) setData(await res.json());
        else toast.error("Failed to load leaderboard");
      } catch (e) {
        toast.error("Failed to load leaderboard");
      } finally {
        setIsLoading(false);
      }
    })();
  }, [scope]);

  const rows = data?.leaderboard || [];
  const me = data?.me;
  const meInTop = rows.some((r) => r.user_id === user?.user_id);

  return (
    <div data-testid="leaderboard-page" className="min-h-screen bg-islamic-blue-950">
      <header className="glass border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            data-testid="back-btn"
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 text-slate-400 hover:text-gold-400 transition-colors font-outfit"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <div className="flex items-center gap-3">
            <Trophy className="w-5 h-5 text-gold-400" />
            <h1 className="font-cinzel text-lg font-semibold text-slate-100">Leaderboard</h1>
          </div>
          <div className="w-16" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="flex flex-wrap gap-2 mb-6">
          <Button
            data-testid="scope-global-btn"
            variant={scope === "global" ? "default" : "ghost"}
            onClick={() => setScope("global")}
            className={
              scope === "global"
                ? "btn-gold rounded-full"
                : "rounded-full border border-white/10 text-slate-300 hover:bg-white/5"
            }
          >
            <Globe className="w-4 h-4 mr-1" /> Global
          </Button>
          {groups.map((g) => (
            <Button
              key={g.group_id}
              data-testid={`scope-group-${g.group_id}`}
              variant={scope === g.group_id ? "default" : "ghost"}
              onClick={() => setScope(g.group_id)}
              className={
                scope === g.group_id
                  ? "btn-gold rounded-full"
                  : "rounded-full border border-white/10 text-slate-300 hover:bg-white/5"
              }
            >
              <UsersIcon className="w-4 h-4 mr-1" /> {g.name}
            </Button>
          ))}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20">
            <div className="spinner w-10 h-10" />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-slate-500 font-outfit">No rankings yet — start memorizing to appear here.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => (
              <Row key={r.user_id} row={r} isMe={r.user_id === user?.user_id} />
            ))}
            {!meInTop && me && (
              <>
                <p className="text-center text-slate-600 text-xs font-outfit my-3">…</p>
                <Row row={me} isMe />
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function rankAccent(rank) {
  if (rank === 1) return "text-yellow-300";
  if (rank === 2) return "text-slate-200";
  if (rank === 3) return "text-amber-600";
  return "text-slate-500";
}

function Row({ row, isMe }) {
  return (
    <div
      data-testid={`leaderboard-row-${row.user_id}`}
      className={`glass-card rounded-xl p-4 flex items-center gap-4 ${
        isMe ? "ring-1 ring-gold-400/40" : ""
      }`}
    >
      <div className={`font-cinzel text-xl w-10 text-center ${rankAccent(row.rank)}`}>
        #{row.rank}
      </div>
      <div className="w-10 h-10 rounded-full bg-gold-400/10 flex items-center justify-center text-gold-400 font-cinzel">
        {row.name?.[0]?.toUpperCase() || "?"}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-outfit text-slate-100 truncate">
          {row.name}
          {isMe && <span className="text-slate-500 text-xs ml-2">(you)</span>}
        </p>
        <div className="flex flex-wrap gap-3 mt-1 text-xs font-outfit text-slate-500">
          <span className="flex items-center gap-1"><BookOpen className="w-3 h-3" /> {row.ayahs} ayahs</span>
          <span>{row.surahs_completed} surahs</span>
          <span className="flex items-center gap-1"><Flame className="w-3 h-3" /> {row.streak}</span>
        </div>
      </div>
      <div className="text-right">
        <p className="font-cinzel text-gold-400 text-lg">{row.points.toLocaleString()}</p>
        <p className="text-slate-500 text-xs font-outfit">points</p>
      </div>
    </div>
  );
}
